import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';
import _ from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import Handler from '../../Handler';
import API from '../../api';
import {
  Check,
  DatePickerBox,
  FileUploader,
  MethodologyDocuments,
  MethodologyStageSteps,
  ModalOpenMethodologyConsultation,
  SelectBox,
  TextInput,
} from '../../components';
import { DialogContext, SnackbarContext, UserContext } from '../../contexts';
import { useForm } from '../../hooks';

export const MethodologyDetailPage = (props) => {
  const history = useHistory();
  const { slug } = useParams();
  const { dialog, updateDialog } = useContext(DialogContext);
  const { updateSnackbar } = useContext(SnackbarContext);
  const { authorityInfo, userInfo, businessInfo, isSuper } = useContext(UserContext);
  const [methodologyInfo, setMethodologyInfo] = useState(null);
  const [consultationOpen, setConsultationOpen] = useState(false);
  const [isAuditor, setIsAuditor] = useState(false);
  const [initData, setInitData] = useState(false);
  // ITS-200. 관련 문서 목록(등록부 문서 · 공개논평 draft).
  const [documents, setDocuments] = useState([]);
  // ITS-200(리뷰 반영). 단계 정보를 initData 와 분리 — reloadDetail 이 initData 전체를 갈아끼우면
  // formData 채움 useEffect([initData, methodologyInfo])가 다시 돌아 미저장 편집이 사라진다.
  const [stageInfo, setStageInfo] = useState(null);
  const [subAreaHold, setSubAreaHold] = useState(false); //초기 렌더링시에 subArea useEffect로 사라지는거 방지;
  const [formData, formMeta, formChange, setFormData, setFormMeta, formReset] = useForm(
    {
      isView: true,
      isViewRegistry: false,
      status: false,
      adminId: '',
      auditor: '',
      version: '',
      title: '',
      titleEn: '',
      standard: '',
      area: '',
      subArea: '',
      startDate: null,
      expiredYear: '',
      mainImage: '',
      introOne: '',
      introOneEn: '',
      introTwo: '',
      introTwoEn: '',
      attachedFile: '',
      terms: [],
      evidence: [],
      calculateFile: '',
      monitoringCalcFile: '',
    },
    {
      isView: {
        key: 'isView',
        label: '노출 여부',
      },
      // ITS-200. 등록부 공개는 승인 게이트가 있는 PATCH 토글(진행 단계)로만 바꾼다 — PUT 저장 경로의 체크박스는 표시 전용.
      isViewRegistry: {
        key: 'isViewRegistry',
        label: '레지스트리 노출',
        disabled: true,
      },
      adminId: {
        key: 'adminId',
        fullWidth: true,
        label: '작성자',
        disabled: true,
      },
      auditor: {
        key: 'auditor',
        fullWidth: true,
        label: '검증기관',
        disabled: true,
      },
      status: {
        key: 'status',
        label: '승인 여부',
        disabled: true,
      },
      version: {
        key: 'version',
        label: '버전',
        fullWidth: true,
        required: true,
      },
      title: {
        key: 'title',
        label: '방법론명',
        fullWidth: true,
        required: true,
      },
      titleEn: {
        key: 'titleEn',
        label: '방법론명(영문)',
        fullWidth: true,
        required: true,
      },
      standard: {
        key: 'standard',
        label: '방법론 기준',
        fullWidth: true,
        required: true,
        menus: [
          {
            value: 'CDM',
            label: 'CDM',
          },
          {
            value: 'Domestic',
            label: '국내 방법론',
          },
          {
            value: 'Oversea',
            label: '국외 방법론',
          },
        ],
      },
      area: {
        key: 'area',
        label: '방법론 감축 분야',
        fullWidth: true,
        required: true,
        menus: [],
      },
      subArea: {
        key: 'subArea',
        label: '방법론 세부분야',
        fullWidth: true,
        required: true,
        menus: [],
      },
      startDate: {
        key: 'startDate',
        label: '유효기간 시작일',
        required: true,
        fullWidth: true,
      },
      expiredYear: {
        key: 'expiredYear',
        label: '유효기간(년수)',
        type: 'number',
        fullWidth: true,
        style: {},
        InputProps: {},
        required: true,
        regexp: Handler.REGEXP.MAX999,
      },
      mainImage: {
        required: true,
        type: 'image',
        key: 'mainImage',
        label: '메인 이미지',
        path: 'methodology/mainImage',
      },
      calculateFile: {
        required: true,
        type: 'file',
        key: 'calculateFile',
        label: '감축량 계산 파일 (내부용)',
        path: 'methodology/calculateFile',
      },
      monitoringCalcFile: {
        required: true,
        type: 'file',
        key: 'monitoringCalcFile',
        label: '모니터링 계산 파일 (내부용)',
        path: 'methodology/monitoringCalcFile',
      },
      introOne: {
        key: 'introOne',
        label: '2줄 소개 (첫째줄)',
        fullWidth: true,
        required: true,
      },
      introOneEn: {
        key: 'introOneEn',
        label: '2줄 소개 (첫째줄) (영문)',
        fullWidth: true,
        required: false,
      },
      introTwo: {
        key: 'introTwo',
        label: '2줄 소개 (둘째줄)',
        fullWidth: true,
        required: false,
      },
      introTwoEn: {
        key: 'introTwoEn',
        label: '2줄 소개 (둘째줄) (영문)',
        fullWidth: true,
        required: false,
      },
      attachedFile: {
        required: true,
        type: 'file',
        key: 'attachedFile',
        label: '첨부파일 (제안서 원본 · 내부용)',
        path: 'methodology/attachedFile',
      },
      terms: {
        key: 'terms',
        mainLabel: '관련 약관',
        labels: [],
        values: [],
      },
      evidence: {
        type: 'evidence',
        key: 'evidence',
        mainLabel: '관련 증빙서류',
        labels: [],
        values: [],
      },
    },
  );

  // useEffect(() => {
  //   if (slug) {
  //     requestNewHistoryList();
  //   }
  // }, []);
  console.log('businessInfo', businessInfo);
  console.log('userInfo', userInfo);
  console.log('businessInfo', businessInfo);
  // const requestNewHistoryList = (init_page) => {
  //   API.Methodology.GetHistoryList(slug, {
  //     page: init_page ? init_page : page,
  //     size: Handler.VARIABLES.PAGE_LIMIT,
  //     ordering: 'DESC',
  //   }).then(
  //     (res) => {
  //       let result = res.data;
  //       console.log('GetHistoryList result', result);
  //       setList(result.content);
  //       setTotalPage(result.totalPages);
  //     },
  //     (err) => {
  //       updateSnackbar({
  //         type: 'error',
  //         open: true,
  //         message: err.data.message,
  //       });
  //     },
  //   );
  // };

  // useEffect(() => {
  //   if (slug) {
  //     requestNewHistoryList();
  //   }
  // }, []);

  useEffect(() => {
    API.Methodology.GetInfo().then(
      (res) => {
        console.log('Getinfo res', res);
        setMethodologyInfo(res.data);
      },
      (err) => {
        console.log('Getinfo err', err);
        updateSnackbar({
          type: 'error',
          open: true,
          message: err.data.message,
        });
      },
    );
  }, []);

  useEffect(() => {
    if (slug && businessInfo) {
      API.Methodology.GetDetail(slug).then(
        (res) => {
          console.log('Methodology res', res);
          let result = res.data;
          if (result.isValid) {
            console.log('Methodology result', result);
            setInitData(result);
            setStageInfo({
              stage: result.stage,
              consultation: result.consultation,
              isViewRegistry: result.isViewRegistry,
              status: result.status,
              attachedFile: result.attachedFile,
            });
          } else {
            updateSnackbar({
              type: 'error',
              open: true,
              message: '유효하지 않은 방법론입니다.',
            });
            history.push('/methodology/list');
          }
        },
        (err) => {
          console.log('err', err);
          updateSnackbar({
            type: 'error',
            open: true,
            message: err.data.message,
          });
        },
      );
    }
  }, [slug, businessInfo]);

  // ITS-200. 관련 문서 목록 조회.
  useEffect(() => {
    if (slug) {
      API.Methodology.GetDocuments(slug).then(
        (res) => {
          setDocuments(res.data || []);
        },
        (err) => {
          updateSnackbar({
            type: 'error',
            open: true,
            message: err.data.message,
          });
        },
      );
    }
  }, [slug]);

  useEffect(() => {
    if (initData && methodologyInfo) {
      console.log('initData', initData);
      let new_meta = _.clone(formMeta);
      new_meta['terms'].labels = Handler.buildTermsLabels(methodologyInfo);
      new_meta['terms'].values = Handler.buildTermsValues(methodologyInfo);
      new_meta['evidence'].labels = Handler.buildEvidenceLabels(methodologyInfo);
      new_meta['evidence'].values = Handler.buildEvidenceValues(methodologyInfo);

      setSubAreaHold(true);
      setFormMeta(new_meta);
      setFormData({
        isView: initData.isView,
        isViewRegistry: initData.isViewRegistry ?? false,
        adminId: initData.adminId,
        version: initData.version,
        title: initData.title,
        titleEn: initData.titleEn,
        area: initData.area,
        subArea: initData.subArea,
        standard: initData.standard,
        status: initData.status,
        auditor: initData.auditor,
        startDate: Handler.dbDateToJsDate(initData.startDate),
        expiredYear: initData.expiredYear,
        mainImage: initData.mainImage,
        calculateFile: initData.calculateFile,
        monitoringCalcFile: initData.monitoringCalcFile,
        introOne: initData.introOne,
        introOneEn: initData.introOneEn,
        introTwo: initData.introTwo,
        introTwoEn: initData.introTwoEn,
        attachedFile: initData.attachedFile,
        // ITS-186. 응답이 수정 요청과 같은 이름(terms/evidence)으로도 연관을 싣게 됐다.
        // 종전 termsMapper/evidenceMapper 도 아직 함께 나오지만, 이름이 어긋나 있던 것이
        // ITS-119 의 원인이었으므로 새 이름 쪽을 쓴다.
        terms: Handler.buildTermsCheckByDB(methodologyInfo, initData.terms),
        evidence: Handler.buildEvidenceCheckByDB(methodologyInfo, initData.evidence),
      });
    }
  }, [initData, methodologyInfo]);

  useEffect(() => {
    if (authorityInfo && userInfo && initData) {
      console.log('>> initData.adminId: ', initData.adminId);
      console.log('>> userInfo.id     : ', userInfo.id);
      setIsAuditor(
        Handler.buildAuthorityValues(authorityInfo).includes(2) && initData.adminId && initData.adminId !== userInfo.id,
      );
      console.log(
        '=======> ',
        Handler.buildAuthorityValues(authorityInfo).includes(2) && initData.adminId && initData.adminId !== userInfo.id,
      );
    }
  }, [authorityInfo, userInfo, initData]);

  useEffect(() => {
    console.log('useEffect businessInfo', businessInfo);

    //사업유형 방법론 분야
    if (businessInfo) {
      let area_menus = [];
      Object.keys(businessInfo).forEach((key) => {
        console.log('key', key);
        let target = businessInfo[key];
        console.log('target', target);
        area_menus.push({
          value: key,
          label: Handler.ENUM.AREA(key),
        });
      });
      let new_meta = _.clone(formMeta);
      new_meta['area'].menus = area_menus;
      setFormMeta(new_meta);
    }
  }, [businessInfo]);

  useEffect(() => {
    //사업유형 방법론 세부분야
    if (formData.area) {
      console.log('change formData.area');
      let sub_area_list = businessInfo[formData.area];
      let sub_area_menus = [];
      for (let i in sub_area_list) {
        let target = sub_area_list[i];
        sub_area_menus.push({
          value: target,
          label: Handler.ENUM.SUB_AREA(target),
        });
      }
      let new_meta = _.clone(formMeta);
      new_meta['subArea'].menus = sub_area_menus;
      setFormMeta(new_meta);
      if (!subAreaHold) {
        formChange(null, 'subArea');
      } else {
        setSubAreaHold(false);
      }
    }
  }, [formData.area]);

  // ITS-119. 약관·증빙 마스터 목록(methodologyInfo)이 아직 안 왔거나 로드에 실패하면
  // formMeta 의 values 가 빈 배열이고, 그 상태로 저장하면 terms/evidence 가 [] 로 나가
  // 서버가 기존 연관을 전삭제한다(빈 배열은 의도적 비우기 경로라 유지되지 않는다).
  // 저장 자체를 막는 것이 유일한 방어다.
  const isMasterListLoaded = () => {
    if (formMeta['terms'].values.length > 0 || formMeta['evidence'].values.length > 0) {
      return true;
    }
    updateSnackbar({
      type: 'error',
      open: true,
      message: '약관·증빙 정보를 불러오지 못했습니다. 새로고침 후 다시 시도하세요',
    });
    return false;
  };

  const requsetSave = (status = false) => {
    if (!isMasterListLoaded()) return;

    let body = Handler.formToBody(formData, formMeta);
    console.log('body', body);

    if (body) {
      body.status = status ? true : body.status;
      let db_body = {
        ...body,
        terms: Handler.buildTermsValueByDB(formMeta['terms'].values, body.terms),
        evidence: Handler.buildEvidenceValueByDB(formMeta['evidence'].values, body.evidence),
      };
      // ITS-200. 등록부 공개는 PATCH 토글 전용 — 저장 요청에 싣지 않는다(서버는 null 이면 기존값 유지).
      delete db_body.isViewRegistry;
      console.log('status', status);
      console.log('db_body', db_body);
      API.Methodology.Put(slug, db_body).then(
        (res) => {
          let result = res.data;
          console.log('Methodology Put result', result);
          updateSnackbar({
            type: 'success',
            open: true,
            message: '저장 성공',
          });
          // ITS-200(리뷰 반영). 저장으로 첨부파일·승인·레지스트리 노출이 바뀔 수 있으므로 스텝을 재조회한다.
          reloadStageInfo();
        },
        (err) => {
          updateSnackbar({
            type: 'error',
            open: true,
            message: err.data.message,
          });
        },
      );
    } else {
      updateSnackbar({
        type: 'error',
        open: true,
        message: '필수정보를 확인하세요',
      });
    }
  };

  const requsetUpdate = () => {
    if (!isMasterListLoaded()) return;

    let body = Handler.formToBody(formData, formMeta);
    console.log('body', body);

    if (body) {
      let db_body = {
        ...body,
        terms: Handler.buildTermsValueByDB(formMeta['terms'].values, body.terms),
        evidence: Handler.buildEvidenceValueByDB(formMeta['evidence'].values, body.evidence),
      };
      delete db_body.adminId;
      console.log('db_body', db_body);
      API.Methodology.PutUpdate(slug, db_body).then(
        (res) => {
          let result = res.data;
          console.log('Methodology PutUpdate result', result);
          history.push('/methodology/detail/' + result.seq);
          updateSnackbar({
            type: 'success',
            open: true,
            message: '저장 성공',
          });
        },
        (err) => {
          updateSnackbar({
            type: 'error',
            open: true,
            message: err.data.message,
          });
        },
      );
    } else {
      updateSnackbar({
        type: 'error',
        open: true,
        message: '필수정보를 확인하세요',
      });
    }
  };

  // ITS-200. 공개논평 개설/등록부 공개 갱신 후 stage 등만 재조회(initData 전체는 건드리지 않는다 — 위 참고).
  const reloadStageInfo = () => {
    API.Methodology.GetDetail(slug).then(
      (res) => {
        let result = res.data;
        setStageInfo({
          stage: result.stage,
          consultation: result.consultation,
          isViewRegistry: result.isViewRegistry,
          status: result.status,
          attachedFile: result.attachedFile,
        });
      },
      (err) => {
        updateSnackbar({
          type: 'error',
          open: true,
          message: err.data.message,
        });
      },
    );
  };

  const requestOpenConsultation = (body) => {
    API.Methodology.PostConsultation(slug, body).then(
      () => {
        updateSnackbar({
          type: 'success',
          open: true,
          message: '공개논평이 개설되었습니다.',
        });
        closeConsultationModal();
        reloadStageInfo();
        // 개설 시 CONSULTATION 문서가 자동 복사되므로 목록을 다시 조회한다.
        API.Methodology.GetDocuments(slug).then(
          (res) => {
            setDocuments(res.data || []);
          },
          (err) => {
            updateSnackbar({
              type: 'error',
              open: true,
              message: err.data.message,
            });
          },
        );
      },
      (err) => {
        updateSnackbar({
          type: 'error',
          open: true,
          message: err.data.message,
        });
      },
    );
  };

  const requestRegistryVisibility = (next) => {
    API.Methodology.PatchRegistryVisibility(slug, { isViewRegistry: next }).then(
      () => {
        updateSnackbar({
          type: 'success',
          open: true,
          message: next ? '등록부에 공개되었습니다.' : '등록부 공개가 해제되었습니다.',
        });
        // 기본정보 폼의 '레지스트리 노출' 체크박스도 함께 동기화한다(다른 미저장 편집은 보존).
        formChange(next, 'isViewRegistry');
        reloadStageInfo();
      },
      (err) => {
        updateSnackbar({
          type: 'error',
          open: true,
          message: err.data.message,
        });
      },
    );
  };

  const onDialogConfirm = (e) => {
    console.log('onDialogConfirm');
    requsetSave(true);
  };

  // ITS-113. 승인 해제는 저장(수정) 경로로는 되지 않는다 — 서버가 status=false 를 받아도
  // 승인을 유지하는 단방향 래치라서, 해제 전용 API 를 호출해야 한다.
  const requsetRevokeApproval = () => {
    API.Methodology.PutRevokeApproval(slug).then(
      (res) => {
        let result = res.data;
        console.log('Methodology PutRevokeApproval result', result);
        updateSnackbar({
          type: 'success',
          open: true,
          message: '승인이 해제되었습니다.',
        });
        // 해제 결과로 initData 를 갱신하면 아래 useEffect 가 formData 를 다시 채워
        // 승인 여부 체크박스와 버튼 상태가 화면에 반영된다.
        setInitData((prev) => ({ ...prev, status: result.status, auditor: result.auditor }));
        // ITS-200(리뷰 반영). stage 파생값까지 서버에서 다시 받도록 전체 재조회한다.
        reloadStageInfo();
      },
      (err) => {
        updateSnackbar({
          type: 'error',
          open: true,
          message: err.data.message,
        });
      },
    );
  };

  const closeConsultationModal = () => {
    setConsultationOpen(false);
  };

  console.log('formData', formData);
  console.log('formMeta', formMeta);

  return (
    <article className='basic-page'>
      <section className='content-header__container'>
        <header>
          <h3>방법론 상세</h3>
        </header>
        <div className='button__box'>
          {isAuditor && (
            <Button
              variant='contained'
              disabled={formData.status}
              color='success'
              onClick={() => {
                updateDialog({
                  type: 'success',
                  open: true,
                  title: '방법론 승인여부',
                  description: `${formData.title}\n방법론을 승인하시겠습니까?`,
                  onConfirm: onDialogConfirm,
                });
              }}
            >
              방법론 승인
            </Button>
          )}
          {isSuper && formData.status && (
            <Button
              variant='outlined'
              color='error'
              onClick={() => {
                updateDialog({
                  type: 'error',
                  open: true,
                  title: '방법론 승인 해제',
                  description: `${formData.title}\n승인을 해제하시겠습니까?\n해제하면 프로젝트 등록 드롭다운에서 제외됩니다.`,
                  onConfirm: requsetRevokeApproval,
                });
              }}
            >
              승인 해제
            </Button>
          )}
          {isSuper && (
            <>
              <Button variant='contained' onClick={requsetUpdate}>
                버전 업데이트
              </Button>
              <Button variant='contained' onClick={() => requsetSave()}>
                저장
              </Button>
            </>
          )}
        </div>
      </section>
      <section className='form__container'>
        <header>
          <h3>기본 정보</h3>
        </header>
        {!initData ? (
          <div className='loading__container'>
            <CircularProgress></CircularProgress>
          </div>
        ) : (
          <div className='flex'>
            {isSuper && <Check value={formData.isView} onChange={formChange} meta={formMeta.isView}></Check>}
            {isSuper && (
              <Check value={formData.isViewRegistry} onChange={formChange} meta={formMeta.isViewRegistry}></Check>
            )}
            {isSuper && (
              <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 1 }}>
                레지스트리 노출은 아래 「진행 단계」의 등록부 공개 버튼으로만 바꿉니다(정식 승인 후 가능).
              </Typography>
            )}
            <Check value={formData.status} onChange={formChange} meta={formMeta.status}></Check>
            <TextInput value={formData.auditor} onChange={formChange} meta={formMeta.auditor}></TextInput>
            <TextInput value={formData.version} onChange={formChange} meta={formMeta.version}></TextInput>
            <TextInput value={formData.title} onChange={formChange} meta={formMeta.title}></TextInput>
            <TextInput value={formData.titleEn} onChange={formChange} meta={formMeta.titleEn}></TextInput>
            <SelectBox value={formData.standard} onChange={formChange} meta={formMeta.standard}></SelectBox>
            <SelectBox value={formData.area} onChange={formChange} meta={formMeta.area}></SelectBox>
            <SelectBox value={formData.subArea} onChange={formChange} meta={formMeta.subArea}></SelectBox>
            <DatePickerBox value={formData.startDate} onChange={formChange} meta={formMeta.startDate}></DatePickerBox>
            <TextInput value={formData.expiredYear} onChange={formChange} meta={formMeta.expiredYear}></TextInput>
            <FileUploader value={formData.mainImage} onChange={formChange} meta={formMeta.mainImage}></FileUploader>
            <TextInput value={formData.introOne} onChange={formChange} meta={formMeta.introOne}></TextInput>
            <TextInput value={formData.introOneEn} onChange={formChange} meta={formMeta.introOneEn}></TextInput>
            <TextInput value={formData.introTwo} onChange={formChange} meta={formMeta.introTwo}></TextInput>
            <TextInput value={formData.introTwoEn} onChange={formChange} meta={formMeta.introTwoEn}></TextInput>
            <FileUploader
              value={formData.attachedFile}
              onChange={formChange}
              meta={formMeta.attachedFile}
            ></FileUploader>
            <FileUploader
              value={formData.calculateFile}
              onChange={formChange}
              meta={formMeta.calculateFile}
            ></FileUploader>
            <FileUploader
              value={formData.monitoringCalcFile}
              onChange={formChange}
              meta={formMeta.monitoringCalcFile}
            ></FileUploader>
            <Check value={formData.terms} onChange={formChange} meta={formMeta.terms}></Check>
            <Check value={formData.evidence} onChange={formChange} meta={formMeta.evidence}></Check>
          </div>
        )}
      </section>

      {initData && stageInfo && (
        <section className='form__container' style={{ marginTop: 40 }}>
          <header>
            <h3>진행 단계</h3>
          </header>
          <MethodologyStageSteps
            stage={stageInfo.stage}
            consultation={stageInfo.consultation}
            status={stageInfo.status}
            isViewRegistry={stageInfo.isViewRegistry}
            attachedFile={stageInfo.attachedFile}
            documents={documents}
            canEdit={isSuper}
            onOpenConsultation={() => setConsultationOpen(true)}
            onToggleRegistry={requestRegistryVisibility}
          ></MethodologyStageSteps>
        </section>
      )}

      {initData && (
        <section className='form__container' style={{ marginTop: 40 }}>
          <header>
            <h3>관련 문서</h3>
          </header>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            {
              '등록부·공개논평 화면에 실제로 공개되는 문서입니다. 공개논평 화면 = 「공개논평 문서 (draft)」 · 등록부 = 「기본 문서」·「결과 문서」. 기본 정보의 첨부파일·계산 파일은 내부용이며 공개되지 않습니다.'
            }
          </Typography>
          <MethodologyDocuments
            seq={slug}
            documents={documents}
            onChange={setDocuments}
            canEdit={isSuper}
          ></MethodologyDocuments>
        </section>
      )}

      <Modal open={consultationOpen} onClose={closeConsultationModal}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 860,
            bgcolor: 'background.paper',
            border: '2px solid #bcbcbc',
            boxShadow: 24,
            p: 0,
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <ModalOpenMethodologyConsultation
            data={initData}
            requestAction={requestOpenConsultation}
            onClose={closeConsultationModal}
          ></ModalOpenMethodologyConsultation>
        </Box>
      </Modal>
    </article>
  );
};
